import { forageStorage } from "../globalApi.svelte";

/*
    MARP (MultiAgent RP) 네이티브 클라이언트

    플러그인판은 사용자가 설정한 서버 URL(예: http://localhost:6009)로
    Risuai.nativeFetch 하여 CORS·무인증 상태로 사이드카를 호출했다. 네이티브판은
    same-origin `/api/multiagent/*`를 checkAuth(risu-auth 헤더) 경유로 호출한다
    → CORS·무인증 문제 해소. 인증 헤더 구성은 nodeStorage.authFetch 및
    wyglore/nativeHost.svelte.ts 의 wlAuthHeaders 와 동일한 규약이다.

    서버 엔드포인트(server.cjs, checkAuth 필수 — Phase 1 서버 포트에서 추가):
      - POST /api/multiagent/analyze          → 핫패스(월드/플롯/캐릭터 분석)
      - GET  /api/multiagent/status           → 사이드카/에이전트 상태
      - GET|PUT /api/multiagent/config        → 에이전트 프로바이더/모델/프롬프트 설정
      - GET  /api/multiagent/prompts/defaults → 기본 프롬프트팩
      - GET|POST /api/multiagent/presets      → 프리셋 목록/생성
      - PUT|DELETE /api/multiagent/presets/:id→ 프리셋 갱신/삭제
      - GET  /api/multiagent/test?agent=...   → 단일 에이전트 LLM 연결 테스트
*/

// ── analyze 요청/응답 계약 (docs/MARP-AND-TURNKEY-PLAN.md 참조) ─────────────

export interface AnalyzeMessage {
    role: string;
    content: string;
}

export interface AnalyzeRequest {
    user_input: string;
    chat_history: AnalyzeMessage[];
    system_context: string;
    analysis_language: string;
    /** 선택: 분석에 사용할 컨텍스트 윈도우(토큰/문자 상한). 서버가 무시 가능. */
    context_window?: number;
}

export interface AnalyzeResponse {
    context_world: string;
    context_plot: string;
    context_char: string;
    errors?: Record<string, string>;
    latency_ms?: number;
}

// ── same-origin 인증 헤더 (wlAuthHeaders / nodeStorage.authFetch 미러) ──────

/** nodeStorage.authFetch 와 동일한 risu-auth 헤더 구성 (same-origin). */
async function maAuthHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
    const headers: Record<string, string> = { "risu-auth": await forageStorage.createAuth() };
    if (extra) Object.assign(headers, extra);
    return headers;
}

function maUrl(suffix: string): string {
    return `/api/multiagent${suffix}`;
}

/** 세션 비활성(423) 이벤트 브릿지 — wyglore 그래프 스토어와 동일 시맨틱. */
function bridgeSessionDeactivated(status: number) {
    if (status === 423 && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("risu-session-deactivated"));
    }
}

async function maGet<T>(suffix: string): Promise<T> {
    const res = await fetch(maUrl(suffix), {
        method: "GET",
        headers: await maAuthHeaders(),
    });
    bridgeSessionDeactivated(res.status);
    if (res.status < 200 || res.status >= 300) {
        throw new Error(`multiagent GET ${suffix} ${res.status}`);
    }
    return res.json() as Promise<T>;
}

async function maSend<T>(method: string, suffix: string, body?: unknown): Promise<T> {
    const res = await fetch(maUrl(suffix), {
        method,
        headers: await maAuthHeaders(body !== undefined ? { "content-type": "application/json" } : undefined),
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    bridgeSessionDeactivated(res.status);
    if (res.status < 200 || res.status >= 300) {
        throw new Error(`multiagent ${method} ${suffix} ${res.status}`);
    }
    return res.json() as Promise<T>;
}

// ── API 메서드 ─────────────────────────────────────────────────────────────

// 서버 파이프라인 최악치(에이전트 request_timeout 60s × 2 스테이지 순차)보다
// 약간 큰 상한. 정상 분석은 절대 안 끊기고, 프록시 블랙홀 등으로 응답이 영영
// 안 오는 병리적 케이스에서만 채팅이 무한 대기하는 것을 막는다(fail-open 보장).
const ANALYZE_TIMEOUT_MS = 130_000;

/**
 * 핫패스. 월드/플롯/캐릭터 3개 보조 에이전트 분석을 서버에서 실행한다.
 * 실패는 호출부(index.ts hook)에서 fail-open 으로 처리한다 — 여기서는 throw.
 */
export async function analyze(body: AnalyzeRequest): Promise<AnalyzeResponse> {
    const res = await fetch(maUrl("/analyze"), {
        method: "POST",
        headers: await maAuthHeaders({ "content-type": "application/json" }),
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(ANALYZE_TIMEOUT_MS),
    });
    bridgeSessionDeactivated(res.status);
    if (res.status < 200 || res.status >= 300) {
        throw new Error(`multiagent POST /analyze ${res.status}`);
    }
    return res.json() as Promise<AnalyzeResponse>;
}

export async function getStatus<T = any>(): Promise<T> {
    return maGet<T>("/status");
}

export async function getConfig<T = any>(): Promise<T> {
    return maGet<T>("/config");
}

export async function putConfig<T = any>(config: unknown): Promise<T> {
    return maSend<T>("PUT", "/config", config);
}

export async function getPromptsDefaults<T = any>(): Promise<T> {
    return maGet<T>("/prompts/defaults");
}

// ── 프리셋 CRUD ────────────────────────────────────────────────────────────

export async function listPresets<T = any>(): Promise<T> {
    return maGet<T>("/presets");
}

export async function createPreset<T = any>(preset: unknown): Promise<T> {
    return maSend<T>("POST", "/presets", preset);
}

export async function updatePreset<T = any>(id: string, preset: unknown): Promise<T> {
    return maSend<T>("PUT", `/presets/${encodeURIComponent(id)}`, preset);
}

export async function deletePreset<T = any>(id: string): Promise<T> {
    return maSend<T>("DELETE", `/presets/${encodeURIComponent(id)}`);
}

// ── 단일 에이전트 연결 테스트 ───────────────────────────────────────────────

/** agent: 'world' | 'plot' | 'char' (서버가 라벨/응답 규약 소유). */
export async function test<T = any>(agent: string): Promise<T> {
    return maGet<T>(`/test?agent=${encodeURIComponent(agent)}`);
}
